<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>{{ $t('travelOffer.planSubtitle') }} {{ $t('travelOffer.requiredFields') }}</span>
      </div>
    </div>

    <!-- ===== SÉLECTION ===== -->
    <div class="ov-card mb-6">
      <div class="ov-card__body">
        <div class="ov-row">
          <div v-if="$store.state.userAuthentified.roleCode == 'RoleAdminSocieteMere'" class="ov-field">
            <label class="ov-label">{{ $t('travelOffer.transportCompany') }}<span class="ov-required">*</span></label>
            <select
              v-model="compagnieTransportRaisonSociale"
              class="ov-select"
              @change="getAllAvailableStationByCompagnie(compagnieTransportRaisonSociale)"
            >
              <option :value="null" disabled>Sélectionnez une compagnie</option>
              <option v-for="(compagnie, index) in compagnieTransportList" :key="index" :value="compagnie.raisonSociale">
                {{ compagnie.raisonSociale }}
              </option>
            </select>
          </div>

          <div class="ov-field">
            <label class="ov-label">{{ $t('travelOffer.travelOffer') }}<span class="ov-required">*</span></label>
            <select
              v-model="offreVoyageDesignation"
              class="ov-select"
              @change="getProgrammeByOffreVoyage(offreVoyageDesignation)"
            >
              <option :value="null" disabled>Sélectionnez une offre de voyage</option>
              <option v-for="(offre, index) in offreVoyageParCompagnieTransportList" :key="index" :value="offre.designation">
                {{ offre.designation }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!offreVoyageDesignation" class="ov-empty pl-empty">
      Sélectionnez une offre de voyage pour voir ses programmes et en planifier de nouveaux.
    </div>

    <template v-else>
      <div class="ov-grid">
        <div class="ov-stack">
          <!-- ===== NOUVEAU PROGRAMME ===== -->
          <div class="ov-card">
            <div class="ov-card__head">
              <div>
                <div class="ov-card__title">Nouveau programme</div>
                <div class="ov-card__sub">{{ offreVoyageDesignation }}</div>
              </div>
            </div>

            <div class="ov-card__body">
              <div class="ov-row">
                <div class="ov-field">
                  <label class="ov-label">Date de départ<span class="ov-required">*</span></label>
                  <input
                    v-model.trim="$v.programmeOffreVoyageModel.dateDepart.$model"
                    type="date"
                    :min="minDepartureDate"
                    class="ov-input"
                  />
                  <div class="ov-hint">La date de départ ne peut pas être dans le passé.</div>
                </div>

                <div class="ov-field">
                  <label class="ov-label">Date d'arrivée<span class="ov-required">*</span></label>
                  <input
                    v-model.trim="$v.programmeOffreVoyageModel.dateArrivee.$model"
                    type="date"
                    :min="programmeOffreVoyageModel.dateDepart || minDepartureDate"
                    class="ov-input"
                  />
                  <div class="ov-hint">Égale ou postérieure à la date de départ.</div>
                </div>

                <div class="ov-field">
                  <label class="ov-label">Heure de départ<span class="ov-required">*</span></label>
                  <input
                    v-model.trim="$v.programmeOffreVoyageModel.heureDepart.$model"
                    type="time"
                    class="ov-input"
                  />
                </div>

                <div class="ov-field">
                  <label class="ov-label">Heure d'arrivée (estimation)<span class="ov-required">*</span></label>
                  <input
                    v-model.trim="$v.programmeOffreVoyageModel.heureArrivee.$model"
                    type="time"
                    :min="minArrivalTime"
                    class="ov-input"
                  />
                  <div v-if="isSameDay" class="ov-hint ov-hint--warning">
                    Voyage le même jour : l'arrivée doit être après {{ programmeOffreVoyageModel.heureDepart }}.
                  </div>
                </div>

                <div class="ov-field ov-col-full">
                  <label class="ov-label">Autocar<span class="ov-required">*</span></label>
                  <select
                    v-model.trim="$v.programmeOffreVoyageModel.numeroBus.$model"
                    class="ov-select"
                    @change="getBusDetail(programmeOffreVoyageModel.numeroBus)"
                  >
                    <option :value="null" disabled>Choisissez un autocar</option>
                    <option v-for="bus in availableBusList" :key="bus.id" :value="bus.id">
                      {{ bus.numero }}
                    </option>
                  </select>
                  <template v-if="programmeOffreVoyageModel.numeroBus">
                    <div v-if="programmesByBus.programmeDTOS" class="ov-hint ov-hint--danger">
                      Ce bus est déjà associé à d'autres programmes.
                    </div>
                    <div v-else class="ov-hint ov-hint--success">Ce bus est disponible.</div>
                  </template>
                </div>
              </div>
            </div>

            <div class="ov-card__foot">
              <button type="button" class="ov-btn ov-btn--secondary" @click="reinitialiseFormPlanification">
                <v-icon small>mdi-restore</v-icon>
                {{ $t('travelOffer.reset') }}
              </button>
              <button type="button" class="ov-btn ov-btn--navy" @click="savePlanningInList">
                <v-icon small>mdi-plus</v-icon>
                {{ $t('travelOffer.addProgram') }}
              </button>
            </div>
          </div>

          <!-- ===== PROGRAMMES DU BUS ===== -->
          <div v-if="programmeOffreVoyageModel.numeroBus" class="ov-card">
            <div class="ov-card__head">
              <div>
                <div class="ov-card__kicker">Disponibilité</div>
                <div class="ov-card__title">
                  {{ $t('travelOffer.programsAssociatedToBus') }}
                  {{ getDesignationBusById(programmeOffreVoyageModel.numeroBus) }}
                </div>
              </div>
            </div>
            <v-data-table
              class="ov-table"
              :headers="headersProgrammeAvailable"
              :items="programmesByBus.programmeDTOS || []"
              :loading="loadingForProgrammeTable"
              loader-height="2"
              no-data-text="Aucun programme pour ce bus"
            >
              <template v-slot:[`item.isExpired`]="{ item }">
                <span v-if="item.isExpired" class="ov-badge ov-badge--danger">{{ $t('travelOffer.expired') }}</span>
                <span v-else class="ov-badge ov-badge--success">{{ $t('travelOffer.inProgress') }}</span>
              </template>
            </v-data-table>
          </div>

          <!-- ===== PROGRAMMES DÉJÀ ASSOCIÉS ===== -->
          <div class="ov-card">
            <div class="ov-card__head">
              <div>
                <div class="ov-card__kicker">Existant</div>
                <div class="ov-card__title">
                  Programmes déjà associés
                  <span class="ov-count">{{ programmeAvailables.length }}</span>
                </div>
              </div>
              <div class="ov-search">
                <v-icon small>mdi-magnify</v-icon>
                <input v-model="searchForProgrammeTable" type="text" class="ov-input" :placeholder="$t('travelOffer.search')" />
              </div>
            </div>
            <v-data-table
              class="ov-table"
              :headers="headersProgrammeAvailable"
              :items="programmeAvailables"
              :loading="loadingForProgrammeTable"
              :search="searchForProgrammeTable"
              loader-height="2"
              no-data-text="Aucun programme associé"
            >
              <template v-slot:[`item.isExpired`]="{ item }">
                <span v-if="item.isExpired" class="ov-badge ov-badge--danger">{{ $t('travelOffer.expired') }}</span>
                <span v-else class="ov-badge ov-badge--success">{{ $t('travelOffer.inProgress') }}</span>
              </template>
            </v-data-table>
          </div>
        </div>

        <!-- ===== RÉSUMÉ ===== -->
        <div class="ov-sticky">
          <div class="ov-card">
            <div class="ov-card__head">
              <div class="ov-card__title">{{ $t('travelOffer.summary') }}</div>
            </div>
            <div class="ov-card__body">
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('travelOffer.selectedOffer') }}</span>
                <span class="ov-stat__value">{{ offreVoyageDesignation }}</span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('travelOffer.existingPrograms') }}</span>
                <span class="ov-stat__value">{{ programmeAvailables.length }}</span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('travelOffer.toSchedule') }}</span>
                <span class="ov-stat__value ov-stat__value--accent">{{ programmeOffreVoyageList.length }}</span>
              </div>
              <div class="ov-stat">
                <span class="ov-stat__label">{{ $t('travelOffer.selectedBus') }}</span>
                <span class="ov-stat__value">
                  {{ programmeOffreVoyageModel.numeroBus ? getDesignationBusById(programmeOffreVoyageModel.numeroBus) : '—' }}
                </span>
              </div>
              <div class="ov-note mt-4">
                Ajoutez un ou plusieurs programmes, puis validez-les avec « {{ $t('travelOffer.schedule') }} ».
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== PROGRAMMES À VALIDER ===== -->
      <div v-if="programmeOffreVoyageList.length != 0" class="ov-card mt-6 pl-pending">
        <div class="ov-card__head">
          <div>
            <div class="ov-card__kicker">En attente</div>
            <div class="ov-card__title">
              Programmes à planifier
              <span class="ov-count">{{ programmeOffreVoyageList.length }}</span>
            </div>
          </div>
        </div>
        <div class="ov-card__body">
          <div class="pl-programs">
            <ProgrammeAddedItem
              v-for="(programme, pr) in programmeOffreVoyageList"
              :key="pr"
              :programme="programme"
              :deleteItem="() => deleteItemProgramm(pr)"
            />
          </div>
        </div>
        <div class="ov-card__foot">
          <button type="button" class="ov-btn ov-btn--secondary" @click="programmeOffreVoyageList = []">
            {{ $t('travelOffer.cancel') }}
          </button>
          <button type="button" class="ov-btn ov-btn--primary" @click="enregistrerProgrammeOffreVoyage">
            <v-icon small>mdi-check</v-icon>
            {{ $t('travelOffer.schedule') }}
          </button>
        </div>
      </div>
    </template>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
    <v-alert class="myalert alert-success" type="success" width="350px" dismissible>{{ successMsg }}</v-alert>
    <v-overlay :value="overlay"><v-progress-circular indeterminate size="56" color="#159e72"></v-progress-circular></v-overlay>
  </div>
</template>

<script>
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ProgrammeAddedItem from "./programme/widgets/ProgrammeAddedItem.vue";
import {
  API_CREER_PROGRAMME_OFFRE_VOYAGE,
  API_RECUPERER_LISTE_OFFRE_VOYAGE,
  API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE,
  HEADERS,
  API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
  API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE,
  API_GET_DETAIL_BUS,
} from "@/components/globalConfig/globalConstConfig";
import {
  showErrorMessage,
  showSuccessMessage,
  showWarningMessage,
} from "../messages/messageProcess";

export default {
  name: "PlanifierOffreVoyage",
  components: {
    ProgrammeAddedItem,
  },
  data() {
    return {
      compagnieTransportList: [],
      compagnieTransportRaisonSociale: null,

      errorMsg: null,
      successMsg: null,
      loadingForProgrammeTable: false,
      searchForProgrammeTable: "",
      overlay: false,
      dialogForPlanning: false,
      editedIndex: -1,

      offreVoyageDesignation: null,
      options: {
        index: 0,
        size: 8,
      },

      // VARIABLES PLANIFICATION OFFRE DE VOYAGE

      search: "",
      headers: [
        { text: "RÉFÉRENCE", value: "designation" },
        { text: "NUMERO DU BUS", value: "numeroBus" },
        { text: "DATE DEPART", value: "dateDepart" },
        { text: "DATE ARRIVEE", value: "dateArrivee" },
        { text: "VOYAGE", value: "offreVoyageDesignation" },
        { text: "ACTIONS", value: "actions", sortable: false },
      ],

      headersProgrammeAvailable: [
        { text: "Réference", value: "designation" },
        { text: "N° Bus", value: "numeroBus" },
        { text: "Date de départ", value: "dateDepart" },
        { text: "Date d'arrivée", value: "dateArrivee" },
        { text: "Status", value: "isExpired" },
      ],

      programmeObject: {
        datas: [],
      },

      busDetailObject: {
        data: {
          id: null,
        },
      },

      programmesByBus: {},

      programmeOffreVoyageList: [],

      programmeOffreVoyageModel: {
        offreVoyageDesignation: null,
        designation: null,
        numeroBus: null,
        dateDepart: null,
        dateArrivee: null,
        heureDepart: null,
        heureArrivee: null,
      },

      availableBusList: [],

      busObject: {
        data: {
          raisonSociale: null,
        },
      },

      // VARIABLES POUR LA GESTION DU PRIX ET DU MODE D'UNE OFFRE DE VOYAGE

      objectToSend: {
        datas: [],
      },

      offreObject: {
        data: {
          designation: null,
        },
      },

      offreVoyageParCompagnieTransportList: [],

      offreVoyageObject: {
        data: {
          compagnieTransportRaisonSociale: null,
        },
      },

      programmeAvailables: [],
    };
  },

  validations: {
    // VALIDATION POUR LA PLANIFICATION D'UNE OFFRE DE VOYAGE
    programmeOffreVoyageModel: {
    
      dateDepart: {
        required,
      },

      dateArrivee: {
        required,
      },

      heureArrivee: {
        required,
      },
      heureDepart: {
        required,
      },

      numeroBus: {
        required,
      },
    },
  },

  methods: {
    //Obtenir la liste des compagnies de transport disponibles
    async getCompagnieTransportList() {
      if (
        this.$store.state.userAuthentified.roleCode == "RoleAdminSocieteMere"
      ) {
        axios
          .post(
            API_OBTENIR_LISTE_DES_DEMANDES_ADHESIONS_VALIDEES,
            this.options,
            {
              headers: HEADERS(this.$store.state.userAuthentified.token),
            }
          )
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code != 800) {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              } else {
                this.compagnieTransportList = response.data.items;
              }
            } else {
              this.errorMsg = "Erreur lors de la récupération des données";
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          });
      }
    },

    getDesignationBusById(busId) {
      var designationBus = "";
      this.availableBusList.forEach((element) => {
        if (element.id == busId) {
          designationBus = element.designation;
        }
      });
      return designationBus;
    },

    getNumeroBusByDesignation(designationBus) {
      var busNumber = "";
      this.availableBusList.forEach((element) => {
        if (element.designation === designationBus) {
          busNumber = element.numero;
        } else {
          busNumber = null;
        }
      });
      return busNumber;
    },

    getNumeroBusByBusId(busId) {
      var busNumber = "";
      this.availableBusList.forEach((element) => {
        if (element.id === busId) {
          busNumber = element.numero;
        }
      });
      return busNumber;
    },

    // Obtenir les programmes déjà associés à un bus
    async getBusDetail(busId) {
      this.busDetailObject.data.id = busId;
      await axios
        .post(API_GET_DETAIL_BUS, this.busDetailObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.programmesByBus = response.data.item;
          } else {
            this.programmesByBus = null;
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {});
    },

    // Obtenir liste des programmes disponibles par offre de voyage
    async getProgrammeByOffreVoyage(offreVoyageDesignation) {
      this.loadingForProgrammeTable = true;
      this.offreObject.data.designation = offreVoyageDesignation;
      await axios
        .post(API_RECUPERER_PROGRAMME_PAR_OFFRE_VOYAGE, this.offreObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.data.status.code == 800) {
            this.programmeAvailables = response.data.items;
          } else {
            this.programmeAvailables = [];
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        })
        .finally(() => {
          this.loadingForProgrammeTable = false;
        });
    },

    // OUVERTURE DE LA BOITE DE DIALOG
    openDialogBox() {
      if (
        this.offreVoyageDesignation == null ||
        this.offreVoyageDesignation == ""
      ) {
        this.errorMsg = "Veuillez sélectionner une offre de voyage";
        showErrorMessage();
      } else {
        this.dialogForPlanning = true;
      }
    },

    // RETOURNER LA DATE SOUS FORME JJ-MM-AAAA
    returnDateFormatted(dateFormatted) {
      let date = new Date(dateFormatted);
      let jour = date.getDate();
      let mois = date.getMonth() + 1;
      let annee = date.getFullYear();
      jour = String(jour).padStart(2, "0");
      mois = String(mois).padStart(2, "0");
      let dateFormat = `${jour}/${mois}/${annee}`;
      return dateFormat;
    },

    // Reinitailise le formulaire
    reinitialiseFormPlanification() {
      this.programmeOffreVoyageModel.numeroBus = null;
      this.programmeOffreVoyageModel.offreVoyageDesignation = null;
      this.programmeOffreVoyageModel.heureArrivee = null;
      this.programmeOffreVoyageModel.heureDepart = null;
      this.programmeOffreVoyageModel.dateDepart = null;
      this.programmeOffreVoyageModel.dateArrivee = null;
    },

    // ENREGISTREMENT D'UNE PLANIFICATION
    savePlanningInList() {
      this.$v.$touch();
      if (this.$v.programmeOffreVoyageModel.$invalid) {
        this.errorMsg =
          "Veuillez remplir le formulaire de la planification correctement !!!";
        showErrorMessage();
      } else {
        var planning = {
          offreVoyageDesignation: null,
          numeroBus: null,
          dateDepart: null,
          dateArrivee: null,
          heureDepart: null,
          heureArrivee: null,
        };
        planning.offreVoyageDesignation = this.offreVoyageDesignation;
        planning.numeroBus = this.getNumeroBusByBusId(
          this.programmeOffreVoyageModel.numeroBus
        );

        planning.dateDepart = this.returnDateFormatted(
          this.programmeOffreVoyageModel.dateDepart
        );
        planning.dateArrivee = this.returnDateFormatted(
          this.programmeOffreVoyageModel.dateArrivee
        );
        planning.heureDepart = this.programmeOffreVoyageModel.heureDepart;
        planning.heureArrivee = this.programmeOffreVoyageModel.heureArrivee;

        this.programmeOffreVoyageList.push(planning);
        this.dialogForPlanning = false;

        this.reinitialiseFormPlanification();
      }
    },

    // Surrpimer un programme
    deleteItemProgramm(position) {
      this.programmeOffreVoyageList.splice(position, 1);
    },

    //ENREGISTRER UN PROGRAMME POUR UNE OFFRE DE VOYAGE
    async enregistrerProgrammeOffreVoyage() {
      this.programmeOffreVoyageList.forEach((element) => {
        this.programmeObject.datas.push(element);
      });
      if (this.programmeOffreVoyageList.length == 0) {
        this.errorMsg = "Vous n'avez défini aucun programme !!";
        showErrorMessage();
      } else {
        this.overlay = true;
        await axios
          .post(API_CREER_PROGRAMME_OFFRE_VOYAGE, this.programmeObject, {
            headers: HEADERS(this.$store.state.userAuthentified.token),
          })
          .then((response) => {
            if (response.status == 200) {
              if (response.data.status.code == 800) {
                this.successMsg = response.data.status.message;
                showSuccessMessage();
              } else {
                this.errorMsg = response.data.status.message;
                showErrorMessage();
              }
            } else if (response.status == 204) {
              this.warningMsg =
                "Erreur , lors de la création de l'offre de voyage";
              showWarningMessage();
            } else {
              this.errorMsg = "Erreur , opération de création impossible";
              showErrorMessage();
            }
          })
          .catch((e) => {
            this.errorMsg = e;
            showErrorMessage();
          })
          .finally(() => {
            this.programmeObject.datas = [];
            this.programmeOffreVoyageList = [];
            this.offreVoyageDesignation = null;
            this.overlay = false;
          });
      }
    },

    //OBTENIR LISTE DES OFFRES DE VOYAGES DISPONIBLES PAR COMPAGNIE
    async getAllAvailableBus() {
      this.busObject.data.raisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      this.loading = true;
      await axios
        .post(API_RECUPERER_LISTE_BUS_PAR_COMPAGNIE, this.busObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.availableBusList = response.data.items;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
          showErrorMessage();
        });
    },

    // RECUPERER LA LISTE DES OFFRES DE VOYAGES DISPONIBLES PAR COMPAGNIE DE TRANSPORT
    async obtenirOffreVoyageParCompagnieTransport() {
      this.offreVoyageObject.data.compagnieTransportRaisonSociale =
        this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
      await axios
        .post(API_RECUPERER_LISTE_OFFRE_VOYAGE, this.offreVoyageObject, {
          headers: HEADERS(this.$store.state.userAuthentified.token),
        })
        .then((response) => {
          if (response.status == 200) {
            if (response.data.status.code != 800) {
              this.errorMsg = response.data.status.message;
              showErrorMessage();
            } else {
              this.offreVoyageParCompagnieTransportList = response.data.items;
            }
          } else {
            this.errorMsg = "Erreur";
            showErrorMessage();
          }
        })
        .catch((e) => {
          this.errorMsg = e;
        });
    },

    //VERIFIE SI CEST UN NOMBRE POSITIF
    isNumber(item) {
      return item > 0;
    },
  },

  computed: {
    minDepartureDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    minArrivalTime() {
      // Si la date d'arrivée est la même que la date de départ
      // L'heure d'arrivée doit être après l'heure de départ
      if (
        this.programmeOffreVoyageModel.dateDepart &&
        this.programmeOffreVoyageModel.dateArrivee &&
        this.programmeOffreVoyageModel.dateDepart === this.programmeOffreVoyageModel.dateArrivee &&
        this.programmeOffreVoyageModel.heureDepart
      ) {
        return this.programmeOffreVoyageModel.heureDepart;
      }
      // Sinon, pas de contrainte minimum
      return null;
    },
    isSameDay() {
      return (
        this.programmeOffreVoyageModel.dateDepart &&
        this.programmeOffreVoyageModel.dateArrivee &&
        this.programmeOffreVoyageModel.dateDepart === this.programmeOffreVoyageModel.dateArrivee &&
        this.programmeOffreVoyageModel.heureDepart
      );
    },
  },

  mounted() {
    this.obtenirOffreVoyageParCompagnieTransport();
    this.getAllAvailableBus();
    this.getCompagnieTransportList();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.pl-empty {
  background: #ffffff;
}

.pl-pending {
  border-color: rgba(21, 158, 114, 0.35);
}

.pl-programs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
}
</style>
